import React from "react";
import { Controller, Control, RegisterOptions, useFormContext } from "react-hook-form";
import {
  FormControl,
  Input as MuiInput,
  FormHelperText,
} from "@material-ui/core";
import styled from "styled-components";
import FontStyles from "../../styles/fontStyles";
import { Spacing } from "../../../config";

const Label = styled.p(({ theme, $withError }: any) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.bold14,
    textTransform: "capitalize",
    padding: 0,
    margin: 0,
    color: $withError ? colors.error.main : "#0C396D",
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",

  };
});

const LabelView = styled.div(() => ({
  display: "flex",
  alignContent: "center",
  justifyContent: "flex-start",
}));

const StyledInput = styled(MuiInput)(({ theme, error }: any) => ({
  "&&.MuiInput-root": {
    ...FontStyles.medium14,
    padding: theme.spacing(1.962, 2.5),
    marginTop: "2vh",
    width: "100%",
    textOverflow: "ellipsis",
    color: error ? "red" : theme.palette.text.primary,
    backgroundColor: theme.palette.primary.contrastText,
    borderBottom: "0px",
    borderRadius: 21,
    "& .MuiInputBase-input": {
      padding: 0,
      backgroundColor: "transparent",
      boxShadow: "transparent 0px 0px !important",
    },
  },
}));

const ErrorMessage = styled(FormHelperText)(() => ({
  color: "red",
  minHeight: Spacing(2.5),
}));

export interface InputRHFProps {
  control: Control<any>;
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  mask?: (value: string) => string;
  type?: string;
  maxLength?: number;
  endAdornment?: React.ReactNode;
  rules?: RegisterOptions;
  onBlur?: () => void;
  disabledError?: boolean
}

function InputRHF({
  control,
  name,
  label,
  placeholder,
  disabled,
  readOnly,
  mask,
  type,
  maxLength,
  endAdornment,
  rules,
  onBlur: onBlurCustom,
  disabledError
}: InputRHFProps) {
  const formContext = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;
        return (
          <FormControl fullWidth error={Boolean(errorMsg)}>
            {label && (
              <LabelView>
                <Label $withError={Boolean(errorMsg)}>{label}</Label>
              </LabelView>
            )}
            <StyledInput
              inputProps={{
                maxLength,
                style: {
                  WebkitBoxShadow: "0 0 0 1000px white inset",
                  alignItems: "flex-start",
                },
              }}
              error={Boolean(errorMsg)}
              type={type ?? undefined}
              disableUnderline
              name={name}
              value={mask ? mask(field.value ?? "") : (field.value ?? "")}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              onChange={(e) => {
                const val = mask ? mask(e.target.value) : e.target.value;
                field.onChange(val);
                if (fieldState.invalid) formContext?.trigger(name);
              }}
              onBlur={() => {
                field.onBlur();
                formContext?.trigger(name);
                if (onBlurCustom) onBlurCustom();
              }}
              endAdornment={endAdornment ?? null}
            />
            {!disabledError && <ErrorMessage error={Boolean(errorMsg)}>{errorMsg}</ErrorMessage>}
          </FormControl>
        );
      }}
    />
  );
}

export default InputRHF;
